import { Component, ContentChild, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import dayjs from 'dayjs';
import { Challenge, ChallengeScope, ChallengeType } from '../../services/models/challenges.model';
import { IconSize } from '../../../_shared/components/icon/icon-interface';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import { animationFrameScheduler, count, Observable } from 'rxjs';
import { clamp } from 'lodash';
import { buildProgressAnimation } from '../../../_shared/animations/progress-animation';

@Component({
  selector: 'app-challenges-base-card',
  templateUrl: './challenges-base-card.component.html',
  styleUrls: [
    './challenges-base-card.component.scss',
    './styles/challenges-base-card-header.scss',
    './styles/challenges-base-card-content.scss',
    './styles/challenges-base-card-controls.scss',
  ]
})
export class ChallengesBaseCardComponent<T> implements OnChanges {
  private static readonly COUNT_ANIM_DURATION = 1500;
  count$: Observable<number>;

  @ContentChild('content', { read: TemplateRef }) content: TemplateRef<any>;
  @Input() challenge!: Challenge<T>;
  @Input() createdByLabel!: string;
  @Input() icon!: string;
  @Input() iconSize: IconSize = '1x';
  @Input() progressLabel!: string;
  @Input() showEditMenu: boolean = false;
  @Output() updateChallenge = new EventEmitter<void>();
  @Output() deleteChallenge = new EventEmitter<void>();

  constructor(
    private readonly decimalPipe: DecimalPipe,
    private readonly translateService: TranslateService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.challenge.currentValue) {
      const count = clamp(Math.round(100 * changes.challenge.currentValue.progress / changes.challenge.currentValue.target), 0, 100);
      const startTime = animationFrameScheduler.now();

      this.count$ = buildProgressAnimation(count, startTime, ChallengesBaseCardComponent.COUNT_ANIM_DURATION);
    }
  }

  getChallengeDescriptionLabel(): string {
    if (this.challenge.scope === ChallengeScope.MONTHLY) {
      const translation = this.translateService.instant('challenges.monthlyDescription');
      return translation.replace('{{day_amount}}', this.decimalPipe.transform(30 / this.challenge.target, '1.0-0'));
    }

    if (this.challenge.scope === ChallengeScope.DAILY) {
      const translation = this.translateService.instant('challenges.dailyDescription');
      return translation.replace('{{minutes_amount}}', this.challenge.target);
    }

    switch (this.challenge.type) {
      case ChallengeType.CHECKOUT_TITLES_CHALLENGE:
        return this.translateService.instant('challenges.targetCheckoutTitlesChallenge');

      case ChallengeType.TITLES_READ_CHALLENGE:
        return this.translateService.instant('challenges.targetTitlesReadChallenge');

      case ChallengeType.READING_TIME_CHALLENGE:
        return this.translateService.instant('challenges.targetReadingChallenge');

      default:
        return '';
    }
  }

  getDaysLeft(): number | null {
    if (!this.challenge.expirationDate) {
      return 0;
    }

    return dayjs(this.challenge.expirationDate).diff(new Date(), 'day');
  }

  handleDeleteButtonClick() {
    this.deleteChallenge.emit();
  }

  handleUpdateButtonClick() {
    this.updateChallenge.emit();
  }

  protected readonly count = count;
}
